import Cookies from "js-cookie"
import { call, put, takeEvery, takeLatest } from "redux-saga/effects"
import * as actionTypes from "./actionTypes"
import * as userActions from "./actions"
import { db } from "../../../helpers/firebase/firebaseConfig"
import { collection, getDoc, updateDoc, doc, query, where, getDocs } from "firebase/firestore"
import { errorTranslations } from "./errorTranslations"
import { toast } from "react-toastify"

function setCookieWithExpiration(name, value, days) {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  Cookies.set(name, value, {
    expires: expirationDate, secure: true, sameSite: 'None',
    path: "/"
  });
}

function removeCookie(name) {
  try {
    Cookies.remove(name, { secure: true, sameSite: 'None', path: "/" });
  } catch (error) {
    console.error("Error removing cookies:", error);
  }
}

async function loginUserApi(email, password) {
  try {
    const q = query(
      collection(db, "db_super_admins"),
      where("email", "==", email)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error("Invalid credentials");
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    if (password === userData.password) {
      return { ...userData, id: userDoc.id };
    } else {
      throw new Error("Invalid credentials");
    }
  } catch (error) {
    throw new Error("Invalid credentials");
  }
}

function* loginUser({ payload: { user, history } }) {
  try {
    const res = yield call(loginUserApi, user.email, user.password)

    // Store user data in cookies with an expiration date
    setCookieWithExpiration("userId", res.id, 1);
    setCookieWithExpiration("sessionId", res.id, 1);
    
    // Dispatch login success action with user information
    yield put(userActions.loginSuccess(res))

    // Redirect to dashboard
    toast.success("تم تسجيل الدخول بنجاح")
    history("/dashboard")
  } catch (error) {
    toast.error("بيانات اعتماد غير صحيحة. يرجى التحقق من البريد الإلكتروني وكلمة المرور.")
  }
}

async function getUserProfile(userId) {
  try {
    const userDoc = await getDoc(doc(db, "db_super_admins", userId));
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    const userProfile = {
      full_name: userData.username,
      email: userData.email,
      role: userData.role
    }

    return userProfile
  } catch (error) {
    throw error
  }
}

async function getUserInfo(userId) {
  try {
    const userProfile = await getUserProfile(userId)
    return userProfile
  } catch (error) {
    throw error
  }
}

function* fetchUserInfoSaga(action) {
  try {
    const userInfo = yield call(getUserInfo, action.payload)
    yield put(userActions.storeUserInfo(userInfo))
  } catch (error) {
    // Handle error if needed
  }
}

async function updateInfo(updatedData) {
  try {
    const userId = Cookies.get("sessionId", {
      secure: true, sameSite: 'None',
      path: "/"
    });
    
    const updatedFormData = {
      username: updatedData.full_name,
    }

    await updateDoc(doc(db, "db_super_admins", userId), updatedFormData);
    return { id: userId, ...updatedFormData };
  } catch (error) {
    console.error(error)
    throw error
  }
}

function* updateUser(action) {
  try {
    const updatedData = action.payload
    const updatedInfo = yield call(updateInfo, updatedData)
    yield put({ type: actionTypes.FETCH_USER_INFO, payload: updatedInfo.id })
    yield put(userActions.updateUserInfoSuccess(updatedInfo))
  } catch (error) {
    console.error(error)
  }
}

async function logout(userId) {
  try {
    // Retrieve the token from local storage
    const storedToken = localStorage.getItem('firebaseMessagingToken');

    const userDoc = await getDoc(doc(db, "db_super_admins", userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const existingTokensArray = userData.notification_token || [];
      
      // Remove the token from the array
      const updatedTokensArray = existingTokensArray.filter(token => token !== storedToken);

      // Update the document with the new array
      await updateDoc(doc(db, "db_super_admins", userId), {
        notification_token: updatedTokensArray
      });
    }
  } catch (error) {
    throw error;
  }
}

function* logoutUser({ payload: { history } }) {
  try {
    const userId = Cookies.get("sessionId", {
      secure: true, sameSite: 'None',
      path: "/"
    });
    
    yield call(logout, userId)
    localStorage.removeItem('firebaseMessagingToken');
    localStorage.removeItem('firebaseMessagingTokenDispatched');
    removeCookie("sessionId");
    
    // Dispatch logout success action
    yield put(userActions.logoutUserSuccess())

    // Redirect to login
    history("/login")
  } catch (error) {
    yield put(userActions.apiError(error))
  }
}

async function changPass(data) {
  try {
    const { newPassword } = data.currentPassword;
    const userId = Cookies.get("sessionId", {
      secure: true, sameSite: 'None',
      path: "/"
    });

    await updateDoc(doc(db, "db_super_admins", userId), {
      password: newPassword
    });

    return userId;
  } catch (error) {
    throw new Error("  يرجى التحقق من عنوان بريدك الإلكتروني او كلمة المرور  ");
  }
}

function* changePassword(action) {
  try {
    const response = yield call(changPass, action.payload)
    yield put(userActions.fetchUserInfo(response))
    yield put({ type: actionTypes.CHANGE_PASSWORD_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: actionTypes.CHANGE_PASSWORD_FAILURE, error })
  }
}

async function updateEmail(data) {
  try {
    const userId = Cookies.get("sessionId", {
      secure: true, sameSite: 'None',
      path: "/"
    });
    const { newEmail, password } = data

    const userDoc = await getDoc(doc(db, "db_super_admins", userId));
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    
    if (userData.password === password) {
      await updateDoc(doc(db, "db_super_admins", userId), {
        email: newEmail
      });
      return { id: userId, email: newEmail };
    } else {
      throw new Error("Invalid password");
    }
  } catch (error) {
    throw error;
  }
}

function* changeEmail(action) {
  try {
    const response = yield call(updateEmail, action.payload.newEmail)
    yield put({ type: actionTypes.FETCH_USER_INFO, payload: response.id })
    yield put({ type: actionTypes.CHANGE_EMAIL_SUCCESS })
  } catch (error) {
    const translatedError = errorTranslations[error.message] || error.message
    yield put({
      type: actionTypes.CHANGE_EMAIL_FAILURE,
      error: translatedError,
    })
  }
}

/**
 * Saves the FCM token to the user's document in Firestore
 */
const saveTokenToFirestore = async (token) => {
  try {
    const userId = Cookies.get("userId", {
      secure: true,
      sameSite: 'None',
      path: "/"
    });

    if (!userId) {
      throw new Error('User ID not found');
    }

    const userRef = doc(db, "db_super_admins", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const existingTokens = userData.notification_token || [];

      if (!existingTokens.includes(token)) {
        await updateDoc(userRef, {
          notification_token: [...existingTokens, token]
        });
      }
    }
  } catch (error) {
    console.error('Error saving token to Firestore:', error);
    throw error;
  }
};

/**
 * Handles the token saving logic in Redux saga
 */
function* saveUserTokenSaga(action) {
  try {
    const token = action.payload;
    yield call(saveTokenToFirestore, token);
  } catch (error) {
    console.error('Error in saveUserToken saga:', error);
    // Consider adding error handling or retry logic here
  }
}


function* authSaga() {
  yield takeEvery(actionTypes.LOGIN_USER, loginUser)
  yield takeEvery(actionTypes.LOGOUT_USER, logoutUser)
  yield takeLatest(actionTypes.FETCH_USER_INFO, fetchUserInfoSaga)
  yield takeLatest(actionTypes.UPDATE_USER_INFO_REQUEST, updateUser)
  yield takeEvery(actionTypes.SAVE_USER_TOKEN, saveUserTokenSaga)
  yield takeLatest(actionTypes.CHANGE_PASSWORD_REQUEST, changePassword)
  yield takeLatest(actionTypes.CHANGE_EMAIL_REQUEST, changeEmail)
}

export default authSaga